import type { SetStateAction, Dispatch, PropsWithChildren, FC } from 'react';

import { createContext, useContext, useMemo, useState } from 'react';

import BlockingModal from '.';

interface BlockingModalContextValue {
  /**
   * Contains content that will be displayed inside the modal. When truthy, the
   * modal is toggled open. When set to an empty string, the modal is closed.
   */
  blockingModalValue: string;

  /**
   * Opens or closes this modal by updating its content value.
   * @example setBlockingModalValue('Creating version...');
   * @example setBlockingModalValue('');
   */
  setBlockingModalValue: Dispatch<SetStateAction<BlockingModalContextValue['blockingModalValue']>>;
}

const BlockingModalContext = createContext<BlockingModalContextValue | null>(null);
BlockingModalContext.displayName = 'BlockingModalContext';

/**
 * Renders a blocking non-dismissable modal that is strictly controlled by the
 * `useBlockingModalContext()` hook, which contains state fields to
 * configure the modal.
 *
 * Use this modal to temporarily block interactions with the app while some
 * operation is in progress. For example, when creating a new version, users
 * should be prevented from making additional changes until this completes.
 *
 * We can also use this to tell users that a hard redirect is in progress when
 * we navigate to a different version or branch.
 */
export const BlockingModalProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const [blockingModalValue, setBlockingModalValue] = useState('');

  return (
    <BlockingModalContext.Provider
      value={useMemo(
        () => ({
          blockingModalValue,
          setBlockingModalValue,
        }),
        [blockingModalValue],
      )}
    >
      {children}
      <BlockingModal />
    </BlockingModalContext.Provider>
  );
};

/**
 * Returns state fields used to configure `<BlockingModal>`.
 */
export const useBlockingModalContext = () => {
  const value = useContext(BlockingModalContext);
  if (!value) throw new Error('BlockingModalProvider is missing');
  return value;
};
